import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, Stack, TextField } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import * as React from 'react';
import { useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import useWizardStepContext from 'v2/components/WizardModal/useWizardStepContext';
import { VISITOR_NAME_MAX_LENGTH, WIZARD_STEP_MIN_HEIGHT } from 'v2/constants';
import { emailRegex } from 'v2/utils/validation';

const FORM_ID = 'add-user-wizard-user-details-step-form';
const STEP_INDEX = 0;

export interface UserDetailsFormValues {
  name: string;
  email: string;
}

interface Props {
  onProceed: (data: UserDetailsFormValues) => void;
  existingEmails: string[];
}

const UserDetailsStep: React.FC<Props> = ({ onProceed, existingEmails }) => {
  const { translation } = useTranslation();
  const { goToNext, setFooterContent, steps } = useWizardStepContext();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<UserDetailsFormValues>({ mode: 'onTouched', defaultValues: { name: '', email: '' } });

  const onSubmit = handleSubmit((data) => {
    onProceed(data);
    goToNext();
  });

  useLayoutEffect(() => {
    setFooterContent(
      STEP_INDEX,
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, gap: 2 }}>
        <Button
          key="user-details-step-submit"
          id="user-details-next-button"
          variant="contained"
          type="submit"
          form={FORM_ID}
          endIcon={<ChevronRightIcon />}
        >
          {steps[STEP_INDEX + 1].label}
        </Button>
      </Box>
    );
  }, [setFooterContent, steps, translation]);

  return (
    <Stack
      component="form"
      id={FORM_ID}
      onSubmit={onSubmit}
      noValidate
      gap={3}
      sx={{ minHeight: WIZARD_STEP_MIN_HEIGHT }}
    >
      <TextField
        {...register('name', {
          required: translation.name_cannot_be_empty,
          maxLength: {
            value: VISITOR_NAME_MAX_LENGTH,
            message: translation.name_max_length_error.replace('{{count}}', String(VISITOR_NAME_MAX_LENGTH))
          }
        })}
        id="name-input"
        label={translation.name}
        variant="outlined"
        error={!!errors.name}
        helperText={errors.name?.message}
        autoFocus
      />
      <TextField
        {...register('email', {
          required: translation.email_required_error,
          pattern: { value: emailRegex, message: translation.email_invalid_error },
          validate: (value) => (existingEmails.includes(value) ? translation.email_duplicate_error : true)
        })}
        id="email-input"
        label={translation.e_mail}
        variant="outlined"
        error={!!errors.email}
        helperText={errors.email?.message}
      />
    </Stack>
  );
};

export default UserDetailsStep;
